import { useQuery } from "@apollo/client";
import { FaCoins, FaDiscord, FaLink } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useProfiles } from "../../../context/ProfilesProvider";
import { useDenyWarEnabled } from "../../../hooks/useDenyWarEnabled";
import { GET_USER_DATA } from "../../../queries/mutation/login_query";
import AuthUser from "../../../types/Auth";
import DenyStatus from "../../DenyStatus";
import LoadingPage from "../../LoadingPage";
import StatTile from "../broadcasters/community/StatTile";
import DashCard from "../dashboard/DashCard";
import TopDenyCard from "../dashboard/TopDenyCard";
import TopTippersCard from "../dashboard/TopTippersCard";
import DenyWarBar from "../DenyWarBar";
import CBProfileWidget from "./discord/CBProfileWidget";
import DiscordProfileWidget from "./discord/DiscordProfileWidget";

// The tipper's home: a welcome, their numbers, anything still to link,
// their accounts, then what's happening in the community. DenyStatus and
// DenyWarBar are also OBS widgets; they're used here as-is, only wrapped.
const Home = () => {
  const { cbProfile, discordProfile, cbLoading, discordLoading } =
    useProfiles();
  const { data: userData, loading: userLoading } = useQuery(GET_USER_DATA, {
    fetchPolicy: "cache-and-network",
  });
  const { isDenyWarEnabled } = useDenyWarEnabled();

  // Wait for the profiles too, or the "link your account" prompts flash
  // for everyone while they load.
  if (cbLoading || discordLoading || (userLoading && !userData)) {
    return <LoadingPage />;
  }
  if (!userData) {
    return <div className="p-4">No user</div>;
  }

  const user: AuthUser = userData.getUserData;
  const crimcoins = user?.discordTokens ?? 0;
  const broadcaster = user?.broadcaster ?? "";
  const possessive = broadcaster ? `${broadcaster}'s` : "The";

  return (
    <div className="bg-base-200 min-h-full p-2 sm:p-4 flex flex-col gap-4">
      {/* Who */}
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-wide text-base-content/60">
            Welcome back
          </div>
          <h2 className="text-2xl font-bold text-primary break-all leading-tight">
            {user?.username}
            {user?.username === "fancyturtle" ? " 🐢" : null}
          </h2>
        </div>
        <div className="flex flex-wrap gap-1">
          {cbProfile?.inFanclub && (
            <span className="badge badge-success badge-outline">fan club</span>
          )}
          {cbProfile ? (
            <span className="badge badge-warning badge-outline">
              CB: {cbProfile.username}
            </span>
          ) : (
            <span className="badge badge-ghost">no Chaturbate linked</span>
          )}
          {discordProfile ? (
            <span className="badge badge-primary badge-outline gap-1">
              <FaDiscord /> {discordProfile.username}
            </span>
          ) : (
            <span className="badge badge-ghost gap-1">
              <FaDiscord /> no Discord linked
            </span>
          )}
        </div>
      </header>

      {/* The numbers */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <StatTile
          label="Crimcoins"
          value={crimcoins}
          hint={discordProfile ? "spend them on the tip menu" : "link Discord to use them"}
          className={crimcoins < 1 ? "text-error" : "text-secondary"}
        />
        <StatTile
          label="Your biggest CB tip"
          value={cbProfile ? cbProfile.lifetimeBiggestTip : "—"}
          hint={cbProfile ? undefined : "link Chaturbate to see"}
          className="text-primary"
        />
        <StatTile
          label="CB tips"
          value={cbProfile ? cbProfile.numberOfTips : "—"}
          hint={cbProfile ? "times tipped" : "link Chaturbate to see"}
        />
        <StatTile
          label="Fan club"
          value={cbProfile ? (cbProfile.inFanclub ? "Member" : "Not yet") : "—"}
          className={cbProfile?.inFanclub ? "text-success" : ""}
        />
      </div>

      {/* Still to link */}
      {(!discordProfile || !cbProfile) && (
        <div className="flex flex-col gap-2">
          {!discordProfile && (
            <div className="alert alert-warning py-2 flex-wrap">
              <FaDiscord size={20} />
              <span className="flex-1 min-w-0 text-sm">
                Link your Discord to spend crimcoins, get DMs from the bot and
                the VIP role.
              </span>
              <Link to="/link-accounts" className="btn btn-sm btn-success">
                Link Discord
              </Link>
            </div>
          )}
          {!cbProfile && (
            <div className="alert alert-warning py-2 flex-wrap">
              <FaLink size={20} />
              <span className="flex-1 min-w-0 text-sm">
                Link your Chaturbate account so your tips and fan club status
                count here.
              </span>
              <Link to="/link-accounts" className="btn btn-sm btn-success">
                Link Chaturbate
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Accounts and the community */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
        {cbProfile && <CBProfileWidget broadcaster={broadcaster} />}
        {discordProfile && <DiscordProfileWidget />}
        {discordProfile && (
          <DashCard
            title={
              <>
                <FaCoins className="text-secondary" /> Crimcoins
              </>
            }
            aside={
              <Link to="/shop-crimcoins" className="link link-secondary text-xs">
                Shop
              </Link>
            }
          >
            <p className="text-sm text-base-content/60">
              Redeem items on the tip menu with your balance above.
            </p>
            <Link to="/crimson-menu" className="btn btn-sm btn-primary w-full">
              Open the tip menu
            </Link>
          </DashCard>
        )}
        {cbProfile && (
          <>
            <TopTippersCard />
            <DashCard title={`${possessive} deny status`}>
              <DenyStatus isWidget={false} />
            </DashCard>
            {isDenyWarEnabled && (
              <DashCard title={`${possessive} deny war`} className="md:col-span-2 xl:col-span-3">
                <DenyWarBar />
              </DashCard>
            )}
            <TopDenyCard />
          </>
        )}
      </div>
    </div>
  );
};

export default Home;
